import { Injectable, computed, inject, type Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import { MODULE_NAME } from '../../../models/module-name.token';
import { ImpersonationService } from '../../../services/impersonation.service';
import { AuthSessionStatus } from '../../auth-core/models/auth-session.model';
import { AuthStateService } from '../../auth-core/services/auth-state.service';
import { AUTHENTICATION_ENABLED } from '../../auth-core/tokens/auth-runtime.token';
import { decodeJwt } from '../../auth-core/utils/jwt.util';
import type { IAuthorizationService } from '../interfaces/authorization.interfaces';
import type {
  AuthorizationContext,
  AuthorizationPolicyMap,
  ProtectedRoutePolicy,
} from '../models/authorization-context.model';
import { normalizeStringArray, parseAuthorizationContext } from '../utils/claims-parser.util';

const DEFAULT_CONTEXT: AuthorizationContext = {
  roles: [],
  permissions: [],
  claims: {},
  source: '',
};

// Defensively normalize both inputs — session.permissions has historically been populated from raw JWT claims
// that may arrive as a single string rather than string[]; spreading a raw string here would silently split it
// into individual characters instead of throwing.
function mergePermissions(fromClaims: string[], fromSession: string[]): string[] {
  return Array.from(new Set([...normalizeStringArray(fromClaims), ...normalizeStringArray(fromSession)]));
}

@Injectable({ providedIn: 'root' })
/**
 * Maintains authorization context and evaluates role/permission policies.
 *
 * Context is a `computed()` signal synchronously derived from `AuthStateService.session`. This ensures guards always
 * read the correct permissions in the same tick — no race condition.
 *
 * Exposes context as both a readonly `Signal` (for template / computed use) and a backward-compatible `Observable`
 * stream.
 *
 * **Impersonation Support**: When impersonation is active and shouldApplyImpersonation(moduleName) returns true,
 * permissions are derived from the impersonated user instead of the current admin.
 */
export class AuthorizationService implements IAuthorizationService {
  private readonly authenticationEnabled = inject(AUTHENTICATION_ENABLED, { optional: true }) ?? true;
  private readonly authState = inject(AuthStateService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly moduleName = inject(MODULE_NAME, { optional: true }) ?? 'unknown';
  private _policyMap: AuthorizationPolicyMap | null = null;
  private _context$: Observable<AuthorizationContext> | null = null;

  /**
   * Readonly computed signal — synchronously derived from the session signal. Always up-to-date when read; no async
   * effect cycle required.
   *
   * **Impersonation Support**: If impersonation is active and this module should apply it, permissions are taken from
   * the impersonated user instead of the current session user.
   */
  readonly context = computed<AuthorizationContext>(() => {
    const session = this.authState.session();

    if (session.status !== AuthSessionStatus.SignedIn) {
      return DEFAULT_CONTEXT;
    }

    // Check if impersonation should be applied for this module
    const shouldApplyImpersonation = this.impersonationService.shouldApplyImpersonation(this.moduleName);
    const impersonationState = this.impersonationService.impersonationState();

    // If impersonation is active AND this module should apply it, use impersonated user's permissions
    if (shouldApplyImpersonation && impersonationState.isActive && impersonationState.impersonatedUser) {
      return {
        roles: [impersonationState.impersonatedUser.roleId],
        permissions: normalizeStringArray(impersonationState.impersonatedUser.entitlements),
        claims: {
          userId: impersonationState.impersonatedUser.userId,
          userName: impersonationState.impersonatedUser.userName,
          userEmail: impersonationState.impersonatedUser.userEmail,
          clientId: impersonationState.impersonatedUser.clientId,
          roleId: impersonationState.impersonatedUser.roleId,
        },
        source: 'impersonation',
      };
    }

    // Normal flow: derive from session token
    const tokenClaims = decodeJwt(session.customAccessToken ?? '');

    if (tokenClaims) {
      const parsed = parseAuthorizationContext(tokenClaims);
      return { ...parsed, permissions: mergePermissions(parsed.permissions, session.permissions ?? []) };
    }

    if (session.permissions?.length) {
      return {
        roles: [],
        permissions: mergePermissions([], session.permissions),
        claims: {},
        source: 'auth-session',
      };
    }

    return DEFAULT_CONTEXT;
  });

  /** Observable stream for RxJS consumers. Lazily created to avoid circular dependency during initialization. */
  get context$(): Observable<AuthorizationContext> {
    if (!this._context$) {
      this._context$ = toObservable(this.context);
    }
    return this._context$;
  }

  /** @deprecated Context is now auto-computed from session. This method is a no-op. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setContext(_ctx: AuthorizationContext): void {
    // no-op: context is derived from AuthStateService.session via computed()
  }

  /** Registers route-to-policy mappings for policy-based checks. */
  setPolicyMap(policyMap: AuthorizationPolicyMap): void {
    this._policyMap = policyMap;
  }

  /** Returns true when the current context contains the permission. */
  hasPermission(permission: string): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }
    return this.context().permissions.includes(permission);
  }

  /** Returns true when the current context contains the role. */
  hasRole(role: string): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }

    return this.context().roles.includes(role);
  }

  /** Returns true when any requested role is present. */
  hasAnyRole(roles: string[]): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }
    return roles.some((r) => this.hasRole(r));
  }

  /** Returns true only when all requested roles are present. */
  hasAllRoles(roles: string[]): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }
    return roles.every((r) => this.hasRole(r));
  }

  /** Returns true when any requested permission is present. */
  hasAnyPermission(permissions: string[]): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }
    return permissions.some((p) => this.hasPermission(p));
  }

  /** Returns true only when all requested permissions are present. */
  hasAllPermissions(permissions: string[]): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }
    return permissions.every((p) => this.hasPermission(p));
  }

  /**
   * Returns true when any requested permission prefix matches user permissions. Supports wildcard patterns like
   * 'Admin._', 'Template._'.
   *
   * Examples:
   *
   * - HasAnyPermissionPrefix(['Admin.*']) checks if user has any permission starting with 'Admin.'
   * - HasAnyPermissionPrefix(['Admin._', 'Template._']) checks for either prefix
   * - HasAnyPermissionPrefix(['exact.permission']) works as exact match (backward compatible)
   */
  hasAnyPermissionPrefix(permissionPrefixes: string[]): boolean {
    if (!this.authenticationEnabled) {
      return true;
    }

    const userPerms = this.context().permissions;

    return permissionPrefixes.some((prefix) => {
      // Handle wildcard prefix (e.g., 'Admin.*')
      if (prefix.endsWith('.*')) {
        const basePrefix = prefix.slice(0, -2); // Remove '.*' → 'Admin'
        return userPerms.some((perm) => perm.startsWith(basePrefix + '.'));
      }
      // Exact match (backward compatibility)
      return userPerms.includes(prefix);
    });
  }

  /** Returns route policy metadata when configured. */
  getPolicy(route: string): ProtectedRoutePolicy | undefined {
    return this._policyMap?.routePolicies[route];
  }

  /**
   * Create a reactive permission signal for a specific permission string.
   *
   * Modules use this to create typed permission signals without duplicating factory logic.
   *
   * @param permission Permission string constant (e.g., 'Role.Create', 'Assessment.Start')
   * @returns Computed signal that automatically updates when permissions change
   *
   *   Usage in module auth service: readonly canCreateRole =
   *   this.authService.createPermissionSignal(ADMIN_PERMISSIONS.ROLE_CREATE); // Returns Signal<boolean> that's
   *   reactive to permission changes
   */
  createPermissionSignal(permission: string): Signal<boolean> {
    return computed(() => this.hasPermission(permission));
  }

  /**
   * Create a reactive signal for multiple permissions (AND logic).
   *
   * User must have ALL specified permissions for signal to be true.
   *
   * @param permissions Array of permission string constants (all must be present)
   * @returns Computed signal true only if all permissions are granted
   *
   *   Usage in module auth service: readonly canManageRoles = this.authService.createAllPermissionsSignal([
   *   ADMIN_PERMISSIONS.ROLE_CREATE, ADMIN_PERMISSIONS.ROLE_EDIT, ADMIN_PERMISSIONS.ROLE_DELETE ]);
   */
  createAllPermissionsSignal(permissions: string[]): Signal<boolean> {
    return computed(() => this.hasAllPermissions(permissions));
  }

  /**
   * Create a reactive signal for multiple permissions (OR logic).
   *
   * User must have AT LEAST ONE of the specified permissions for signal to be true.
   *
   * @param permissions Array of permission string constants (any one must be present)
   * @returns Computed signal true if any permission is granted
   *
   *   Usage in module auth service: readonly canModifyTemplate = this.authService.createAnyPermissionSignal([
   *   TEMPLATE_PERMISSIONS.CREATE, TEMPLATE_PERMISSIONS.EDIT ]);
   */
  createAnyPermissionSignal(permissions: string[]): Signal<boolean> {
    return computed(() => this.hasAnyPermission(permissions));
  }
}
